export type Language = 'en' | 'fr';

export type TranslationKeys = typeof en;

const en = {
  // Welcome / Auth
  welcome_slogan: 'Show your talent. Become the NextChamp.',
  sign_up: 'Sign Up',
  log_in: 'Log In',
  welcome_back: 'Welcome back to Nextchamp.',
  join_athletes: 'Join the next generation of athletes.',
  email_or_username: 'Email or Username',
  password: 'Password',
  full_name: 'Full Name',
  first_name: 'First Name',
  last_name: 'Last Name',
  first_name_required: 'First name is required',
  last_name_required: 'Last name is required',
  email: 'Email',
  create_account: 'Create Account',
  no_account: "Don't have an account?",
  have_account: 'Already have an account?',

  // Bottom nav
  nav_feed: 'Feed',
  nav_explore: 'Explore',
  nav_create: 'Create',
  nav_offers: 'Offers',
  nav_messages: 'Messages',

  // Feed
  feed_for_you: 'For You',
  feed_highlights: 'Highlights',
  feed_recruiting: 'Recruiting',
  feed_training: 'Training',
  feed_announcement: 'Announcement',
  feed_matches: 'Matches',
  feed_empty_title: 'Your feed is empty',
  feed_no_posts: 'No {filter} posts yet',
  feed_empty_desc: 'Start by posting your first training or exploring players around you.',
  create_post: 'Create Post',
  new_post: 'New Post',
  post: 'Post',
  whats_happening: "What's happening?",
  category_optional: 'Category (optional)',
  tag_people: 'Tag people',
  people_tagged: '{n} tagged',
  write_caption: 'Write your caption…',
  write_before_publish: 'Write something before publishing',
  post_published: 'Post published!',
  attach_offer: 'Attach an offer',
  attach_offer_optional: 'Attach an offer (optional)',
  change_offer: 'Change offer',
  remove_offer: 'Remove offer',
  select_one_of_your_offers: 'Select one of your active offers',
  no_active_offers: 'You have no active offers yet.',
  offer_attached: 'Offer attached',

  // Profile
  profile: 'Profile',
  user_not_found: 'User not found.',
  followers: 'Followers',
  following: 'Following',
  follow: 'Follow',
  unfollow: 'Unfollow',
  edit_profile: 'Edit Profile',
  message: 'Message',
  posts: 'Posts',
  media: 'Media',
  tagged: 'Tagged',
  saved: 'Saved',
  archived: 'Archived',
  no_posts_yet: 'No posts yet.',
  no_tagged_posts: 'No tagged posts yet.',
  no_saved_posts: 'No saved posts yet.',
  no_archived_posts: 'No archived posts.',
  restore: 'Restore',
  delete: 'Delete',
  season_averages: 'Season Averages',
  invitations: 'Invitations',
  unknown_club: 'Unknown Club',
  unknown: 'Unknown',
  invitation_accepted: 'Invitation accepted!',
  invitation_declined: 'Invitation declined',
  post_restored: 'Post restored',
  post_deleted_perm: 'Post permanently deleted',
  position: 'Position',
  height: 'Height',
  age: 'Age',
  level: 'Level',
  availability: 'Availability',
  speciality: 'Speciality',
  focus: 'Focus',
  experience: 'Experience',
  playstyle: 'Playstyle',

  // Messages
  messages: 'Messages',
  no_conversations: 'No conversations yet',
  start_conversation: "Start a conversation from someone's profile",
  no_messages_yet: 'No messages yet',
  you_prefix: 'You: ',
  shared_a_post: 'Shared a post',

  // Offers
  offers_events: 'Offers & Events',
  create: 'Create',
  active: 'active',
  expired: 'Expired',
  all: 'All',
  tryouts: 'Tryouts',
  recruiting: 'Recruiting',
  camps: 'Camps',
  events: 'Events',
  no_offers: 'No offers yet',
  no_offers_desc: 'Create the first offer or change your filter',
  create_offer: 'Create Offer',
  offer_details: 'Offer Details',
  offer: 'Offer',
  offer_not_found: 'Offer not found.',
  apply_now: 'Apply Now',
  applied: 'Applied ✓',
  closed: 'Closed',
  application_submitted: 'Application submitted!',
  applications: 'Applications',
  no_applications: 'No applications yet.',
  accept: 'Accept',
  reject: 'Reject',
  accepted: 'Accepted',
  rejected: 'Rejected',
  pending: 'Pending',
  applicants: 'applicants',
  applied_to: 'Applied to',
  no_applications_received: 'No applications received yet.',
  my_applications: 'My Applications',
  my_applications_empty: "You haven't applied to any opportunities yet.",
  withdraw_application: 'Withdraw Application',
  withdraw_confirm: 'Withdraw this application?',
  application_withdrawn: 'Application withdrawn',
  applied_on: 'Applied on',
  view_offer: 'View Offer',
  message_organizer: 'Message Organizer',
  contact_creator: 'Contact Creator',
  only_orgs_can_create: 'Only clubs, coaches, recruiters and media can create offers.',
  title: 'Title',
  description: 'Description',
  location: 'Location',
  date: 'Date',
  time: 'Time',
  type: 'Type',
  organizer: 'Organizer',
  number_of_places: 'Number of places',
  price: 'Price',
  positions_wanted: 'Positions wanted (comma-separated)',
  level_optional: 'Level (optional)',
  fill_required_fields: 'Please fill in all required fields',
  offer_created: 'Offer created!',
  places: 'places',
  participants_label: 'Number of participants (optional)',
  participants_helper: 'Maximum number of players allowed to participate in this event.',
  spots_available: 'spots available',
  by: 'by',

  // Notifications
  notifications: 'Notifications',
  mark_all_read: 'Mark all as read',
  no_notifications: 'No notifications yet',
  no_notifications_desc: 'Interact with the app to see notifications appear here',

  // Settings
  settings: 'Settings',
  my_club: 'My Club',
  manage_club: 'Manage club',
  edit_club: 'Edit Club',
  update_club_desc: 'Update name, bio, location',
  request_verification: 'Request Verification',
  verified_badge_club: 'Get a verified badge for your club',
  verified_badge_coach: 'Get a verified coach badge',
  create_club: 'Create Club',
  create_club_desc: 'Set up your club to start recruiting',
  account: 'Account',
  view_profile: 'View Profile',
  subscription: 'Subscription',
  admin_panel: 'Admin Panel',
  switch_account: 'Switch Account',
  log_out: 'Log Out',
  appearance: 'Appearance',
  language: 'Language',
  dark: 'Dark',
  light: 'Light',
  system: 'System',
  app_version: 'NextChamp · current version',
  change_photo: 'Change Photo',
  change_password: 'Change Password',
  preferences: 'Preferences',
  notifications_toggle: 'Notifications',
  privacy: 'Privacy',
  account_visibility: 'Account Visibility',
  account_public: 'Public',
  account_private: 'Private',
  message_permissions: 'Message Permissions',
  msg_everyone: 'Everyone',
  msg_followers: 'Followers only',
  security: 'Security',
  delete_account: 'Delete Account',
  delete_account_confirm: 'Are you sure you want to delete your account? This action cannot be undone.',
  delete_account_title: 'Delete Account',
  cancel: 'Cancel',
  confirm_delete: 'Delete',
  save: 'Save',
  edit: 'Edit',
  publish: 'Publish',
  send: 'Send',
  continue: 'Continue',
  verification: 'Verification',
  verification_pending: 'Pending',
  verification_verified: 'Verified',
  upload_document: 'Upload document',
  support: 'Support',
  help_faq: 'Help / FAQ',
  contact_support: 'Contact Support',
  report_problem: 'Report a Problem',
  contact_us_at: 'Need help? Contact us at',

  // Explore / Network
  explore: 'Explore',
  network: 'Network',
  search_placeholder: 'Search players, clubs, offers...',
  search_name_city: 'Search by name or city...',
  search_clubs_placeholder: 'Search clubs by name, city or level...',
  no_clubs_found: 'No clubs found',
  players_users: 'Players & Users',
  players: 'Players',
  clubs: 'Clubs',
  recruiters: 'Recruiters',
  community_filter: 'Community',
  offers: 'Offers',
  available: 'Available',
  unavailable: 'Unavailable',
  availability_locked_club: 'You are currently registered with a club and cannot be marked as available.',
  browse_all_clubs: 'Browse All Clubs',
  no_results: 'No results found',
  no_results_desc: 'Try a different search term or filter',

  // Admin
  access_denied: 'Access denied',
  admin_only: 'Only admins can view this page',
  users: 'Users',
  coaches: 'Coaches',
  reports: 'Reports',
  cmt_reports: 'Cmt Reports',
  comments: 'Comments',

  // Comments
  add_comment: 'Add a comment…',
  reply: 'Reply',
  replies: 'Replies',
  like: 'Like',
  top: 'Top',
  newest: 'Newest',
  pinned: 'Pinned',

  // Share
  share: 'Share',
  copy_link: 'Copy Link',
  copy_link_desc: 'Copy post link to clipboard',
  share_externally: 'Share Externally',
  share_externally_desc: 'Share via other apps',
  send_in_nextchamp: 'Send in Nextchamp',
  send_in_nextchamp_desc: 'Share with users in the app',
  send_via_message: 'Send via Message',
  send_via_message_desc: 'Share in a conversation',

  // Tagging privacy
  tagging_permissions: 'Who can tag me?',
  tag_everyone: 'Everyone',
  tag_followers: 'Followers only',
  tag_no_one: 'No one',

  // PostCard — actions
  edit_caption: 'Edit caption',
  archive_post: 'Archive post',
  delete_post: 'Delete post',
  view_profile_action: 'View profile',
  save_post: 'Save post',
  unsave_post: 'Unsave post',
  report_post: 'Report post',
  caption_updated: 'Caption updated',
  post_deleted: 'Post deleted',
  post_reported: 'Post reported',
  reposted: 'Reposted to your feed',
  link_copied: 'Link copied to clipboard',
  delete_post_q: 'Delete post?',
  delete_post_desc: 'This action cannot be undone.',
  edit_post: 'Edit Post',
  caption: 'Caption',
  save_changes: 'Save Changes',
  tagged_people: 'Tagged people',
  report_reason_placeholder: 'Why are you reporting this post?',
  report: 'Report',
  with_label: 'With',
  reposted_label: 'reposted',
  now: 'now',

  // Roles
  role_player: 'Player',
  role_coach: 'Coach',
  role_club: 'Club',
  role_recruiter: 'Recruiter',
  role_media: 'Media',
  role_community: 'Community',
  role_admin: 'Admin',

  // Offer types
  offer_type_tryout: 'Tryout',
  offer_type_recruiting: 'Recruiting',
  offer_type_camp: 'Camp',
  offer_type_event: 'Event',
  offer_type_showcase: 'Showcase',
  offer_type_tournament: 'Tournament',

  // Plans
  plan_free: 'Free',
  plan_pro: 'Pro',
  plan_elite: 'Elite',
  plan_club: 'Club',
  popular: 'Popular',
  step_2_of_2: 'Step 2 of 2',
  choose_plan: 'Choose your plan',
  change_plan_later: 'You can always change this later in Settings.',

  // CreateProfile
  edit_your_identity: 'Edit your identity',
  build_player_identity: 'Build your player identity',
  edit_profile_subtitle: 'Update your Nextchamp profile.',
  setup_profile_subtitle: 'Set up your profile. Stand out on the court.',
  identity: 'Identity',
  role: 'Role',
  locked: 'Locked',
  username: 'Username',
  bio: 'Bio',
  bio_placeholder: 'Your story…',
  city_country: 'City, Country',
  required: 'Required',
  profile_updated: 'Profile updated!',
  profile_created: 'Profile created!',
  performance_profile: 'Performance Profile',
  coaching_profile: 'Coaching Profile',
  specialties: 'Specialties',
  coaching_diploma: 'Coaching Diploma (optional)',
  upload_diploma_desc: 'Upload your diploma to get a certified coach badge.',
  diploma_attached: 'Diploma attached',
  pending_verification: 'Pending verification',
  club_registration: 'Club Registration',
  register_your_club: 'Register your club',
  register_club_subtitle: 'Complete all sections. Proof of existence is mandatory.',
  club_account_created: 'Club account created — pending verification.',
  select: 'Select',


  // Common
  teams: 'teams',
  games: 'games',
  game: 'game',
  person: 'person',
  people: 'people',
  loading: 'Loading…',
  back: 'Back',

  // Post categories (canonical English key → label)
  cat_training: 'Training',
  cat_highlight: 'Highlight',
  cat_match: 'Match',
  cat_announcement: 'Announcement',
  cat_recruiting: 'Recruiting',
  cat_player_spotlight: 'Player Spotlight',
  cat_basketball_news: 'Basketball News',
  cat_event_coverage: 'Event Coverage',
  cat_tournament: 'Tournament',
  cat_highlights: 'Highlights',
  cat_community_update: 'Community Update',
  cat_breaking_news: 'Breaking News',
  cat_recruiting_opportunity: 'Recruiting Opportunity',
  cat_prospect_watch: 'Prospect Watch',
  cat_scouting_notes: 'Scouting Notes',
  cat_tryout: 'Tryout',
  cat_showcase_opportunity: 'Showcase Opportunity',
  cat_training_tips: 'Training Tips',
  cat_workout: 'Workout',
  cat_player_development: 'Player Development',
  cat_camp: 'Camp',
  cat_motivation: 'Motivation',

  dm_limit_reached: 'DM limit reached. Upgrade to Pro for unlimited conversations.',
  dm_limit_reached_free: 'You have reached your monthly conversation limit.',
  upgrade: 'Upgrade',
  password_change_soon: 'Password change coming soon',
  account_deleted: 'Account deleted',
  payment_coming_soon: 'Payment coming soon. You can upgrade later from Settings.',
};

const fr: TranslationKeys = {
  welcome_slogan: 'Montre ton talent. Deviens le NextChamp.',
  sign_up: "S'inscrire",
  log_in: 'Se connecter',
  welcome_back: 'Content de te revoir sur Nextchamp.',
  join_athletes: 'Rejoins la nouvelle génération d\'athlètes.',
  email_or_username: 'Email ou nom d\'utilisateur',
  password: 'Mot de passe',
  full_name: 'Nom complet',
  first_name: 'Prénom',
  last_name: 'Nom',
  first_name_required: 'Le prénom est requis',
  last_name_required: 'Le nom est requis',
  email: 'Email',
  create_account: 'Créer un compte',
  no_account: "Tu n'as pas de compte ?",
  have_account: 'Tu as déjà un compte ?',

  nav_feed: 'Feed',
  nav_explore: 'Explorer',
  nav_create: 'Créer',
  nav_offers: 'Offres',
  nav_messages: 'Messages',

  feed_for_you: 'Pour toi',
  feed_highlights: 'À la une',
  feed_recruiting: 'Recrutement',
  feed_training: 'Entraînement',
  feed_announcement: 'Annonces',
  feed_matches: 'Matchs',
  feed_empty_title: 'Ton feed est vide',
  feed_no_posts: 'Pas encore de posts {filter}',
  feed_empty_desc: 'Commence par publier ton premier entraînement ou explore les joueurs autour de toi.',
  create_post: 'Créer un post',
  new_post: 'Nouveau post',
  post: 'Publier',
  whats_happening: 'Quoi de neuf ?',
  category_optional: 'Catégorie (optionnel)',
  tag_people: 'Identifier des personnes',
  people_tagged: '{n} identifié(s)',
  write_caption: 'Écris ta légende…',
  write_before_publish: 'Écris quelque chose avant de publier',
  post_published: 'Post publié !',
  attach_offer: 'Lier une offre',
  attach_offer_optional: 'Lier une offre (optionnel)',
  change_offer: 'Changer d\'offre',
  remove_offer: 'Retirer l\'offre',
  select_one_of_your_offers: 'Sélectionne une de tes offres actives',
  no_active_offers: 'Tu n\'as pas encore d\'offre active.',
  offer_attached: 'Offre liée',

  profile: 'Profil',
  user_not_found: 'Utilisateur introuvable.',
  followers: 'Abonnés',
  following: 'Abonnements',
  follow: 'Suivre',
  unfollow: 'Se désabonner',
  edit_profile: 'Modifier le profil',
  message: 'Message',
  posts: 'Posts',
  media: 'Médias',
  tagged: 'Identifié',
  saved: 'Enregistré',
  archived: 'Archivé',
  no_posts_yet: 'Pas encore de posts.',
  no_tagged_posts: 'Pas encore de posts identifiés.',
  no_saved_posts: 'Pas encore de posts enregistrés.',
  no_archived_posts: 'Pas de posts archivés.',
  restore: 'Restaurer',
  delete: 'Supprimer',
  season_averages: 'Moyennes de saison',
  invitations: 'Invitations',
  unknown_club: 'Club inconnu',
  unknown: 'Inconnu',
  invitation_accepted: 'Invitation acceptée !',
  invitation_declined: 'Invitation refusée',
  post_restored: 'Post restauré',
  post_deleted_perm: 'Post supprimé définitivement',
  position: 'Poste',
  height: 'Taille',
  age: 'Âge',
  level: 'Niveau',
  availability: 'Disponibilité',
  speciality: 'Spécialité',
  focus: 'Spécialisation',
  experience: 'Expérience',
  playstyle: 'Style de jeu',

  messages: 'Messages',
  no_conversations: 'Pas encore de conversations',
  start_conversation: 'Commence une conversation depuis le profil d\'un utilisateur',
  no_messages_yet: 'Pas encore de messages',
  you_prefix: 'Toi : ',
  shared_a_post: 'A partagé un post',

  offers_events: 'Offres & Événements',
  create: 'Créer',
  active: 'actives',
  expired: 'Expirées',
  all: 'Tout',
  tryouts: 'Essais',
  recruiting: 'Recrutement',
  camps: 'Stages',
  events: 'Événements',
  no_offers: 'Pas encore d\'offres',
  no_offers_desc: 'Crée la première offre ou change le filtre',
  create_offer: 'Créer une offre',
  offer_details: 'Détails de l\'offre',
  offer: 'Offre',
  offer_not_found: 'Offre introuvable.',
  apply_now: 'Postuler',
  applied: 'Candidature envoyée ✓',
  closed: 'Fermé',
  application_submitted: 'Candidature envoyée !',
  applications: 'Candidatures',
  no_applications: 'Aucune candidature pour le moment.',
  accept: 'Accepter',
  reject: 'Refuser',
  accepted: 'Accepté',
  rejected: 'Refusé',
  pending: 'En attente',
  applicants: 'candidats',
  applied_to: 'Candidature pour',
  no_applications_received: 'Aucune candidature reçue pour le moment.',
  my_applications: 'Mes candidatures',
  my_applications_empty: "Tu n'as encore postulé à aucune opportunité.",
  withdraw_application: 'Retirer ma candidature',
  withdraw_confirm: 'Retirer cette candidature ?',
  application_withdrawn: 'Candidature retirée',
  applied_on: 'Postulé le',
  view_offer: "Voir l'offre",
  message_organizer: "Contacter l'organisateur",
  contact_creator: 'Contacter le créateur',
  only_orgs_can_create: 'Seuls les clubs, coachs, recruteurs et médias peuvent créer des offres.',
  title: 'Titre',
  description: 'Description',
  location: 'Lieu',
  date: 'Date',
  time: 'Horaire',
  type: 'Type',
  organizer: 'Organisateur',
  number_of_places: 'Nombre de places',
  price: 'Prix',
  positions_wanted: 'Postes recherchés (séparés par des virgules)',
  level_optional: 'Niveau (optionnel)',
  fill_required_fields: 'Remplis tous les champs obligatoires',
  offer_created: 'Offre créée !',
  places: 'places',
  participants_label: 'Nombre de participants (optionnel)',
  participants_helper: 'Nombre maximum de joueurs pouvant participer à cet événement.',
  spots_available: 'places disponibles',
  by: 'par',

  notifications: 'Notifications',
  mark_all_read: 'Tout marquer comme lu',
  no_notifications: 'Pas encore de notifications',
  no_notifications_desc: 'Interagis avec l\'app pour voir les notifications ici',

  settings: 'Paramètres',
  my_club: 'Mon club',
  manage_club: 'Gérer le club',
  edit_club: 'Modifier le club',
  update_club_desc: 'Modifier le nom, la bio, la localisation',
  request_verification: 'Demander la vérification',
  verified_badge_club: 'Obtiens un badge vérifié pour ton club',
  verified_badge_coach: 'Obtiens un badge coach vérifié',
  create_club: 'Créer un club',
  create_club_desc: 'Configure ton club pour commencer à recruter',
  account: 'Compte',
  view_profile: 'Voir le profil',
  subscription: 'Abonnement',
  admin_panel: 'Panneau admin',
  switch_account: 'Changer de compte',
  log_out: 'Se déconnecter',
  appearance: 'Apparence',
  language: 'Langue',
  dark: 'Sombre',
  light: 'Clair',
  system: 'Système',
  app_version: 'NextChamp · version actuelle',
  change_photo: 'Changer la photo',
  change_password: 'Changer le mot de passe',
  preferences: 'Préférences',
  notifications_toggle: 'Notifications',
  privacy: 'Confidentialité',
  account_visibility: 'Visibilité du compte',
  account_public: 'Public',
  account_private: 'Privé',
  message_permissions: 'Permissions de messagerie',
  msg_everyone: 'Tout le monde',
  msg_followers: 'Abonnés uniquement',
  security: 'Sécurité',
  delete_account: 'Supprimer le compte',
  delete_account_confirm: 'Es-tu sûr de vouloir supprimer ton compte ? Cette action est irréversible.',
  delete_account_title: 'Supprimer le compte',
  cancel: 'Annuler',
  confirm_delete: 'Supprimer',
  save: 'Enregistrer',
  edit: 'Modifier',
  publish: 'Publier',
  send: 'Envoyer',
  continue: 'Continuer',
  verification: 'Vérification',
  verification_pending: 'En attente',
  verification_verified: 'Vérifié',
  upload_document: 'Envoyer un document',
  support: 'Assistance',
  help_faq: 'Aide / FAQ',
  contact_support: 'Contacter le support',
  report_problem: 'Signaler un problème',
  contact_us_at: 'Besoin d\'aide ? Contacte-nous à',

  explore: 'Explorer',
  network: 'Réseau',
  search_placeholder: 'Rechercher joueurs, clubs, offres...',
  search_name_city: 'Rechercher par nom ou ville...',
  search_clubs_placeholder: 'Rechercher un club par nom, ville ou niveau...',
  no_clubs_found: 'Aucun club trouvé',
  players_users: 'Joueurs & Utilisateurs',
  players: 'Joueurs',
  clubs: 'Clubs',
  recruiters: 'Recruteurs',
  community_filter: 'Communauté',
  offers: 'Offres',
  available: 'Disponible',
  unavailable: 'Indisponible',
  availability_locked_club: 'Tu es actuellement enregistré dans un club et ne peux pas être marqué comme disponible.',
  browse_all_clubs: 'Voir tous les clubs',
  no_results: 'Aucun résultat',
  no_results_desc: 'Essaie un autre terme ou filtre',

  access_denied: 'Accès refusé',
  admin_only: 'Seuls les admins peuvent voir cette page',
  users: 'Utilisateurs',
  coaches: 'Coachs',
  reports: 'Signalements',
  cmt_reports: 'Signalements com.',
  comments: 'Commentaires',

  add_comment: 'Ajouter un commentaire…',
  reply: 'Répondre',
  replies: 'Réponses',
  like: 'J\'aime',
  top: 'Top',
  newest: 'Récents',
  pinned: 'Épinglé',

  share: 'Partager',
  copy_link: 'Copier le lien',
  copy_link_desc: 'Copier le lien du post',
  share_externally: 'Partager',
  share_externally_desc: 'Partager via d\'autres apps',
  send_in_nextchamp: 'Envoyer dans Nextchamp',
  send_in_nextchamp_desc: 'Partager avec des utilisateurs de l\'app',
  send_via_message: 'Envoyer par message',
  send_via_message_desc: 'Partager dans une conversation',

  tagging_permissions: 'Qui peut me taguer ?',
  tag_everyone: 'Tout le monde',
  tag_followers: 'Abonnés uniquement',
  tag_no_one: 'Personne',

  edit_caption: 'Modifier la légende',
  archive_post: 'Archiver le post',
  delete_post: 'Supprimer le post',
  view_profile_action: 'Voir le profil',
  save_post: 'Enregistrer le post',
  unsave_post: 'Retirer des enregistrés',
  report_post: 'Signaler le post',
  caption_updated: 'Légende mise à jour',
  post_deleted: 'Post supprimé',
  post_reported: 'Post signalé',
  reposted: 'Reposté dans ton feed',
  link_copied: 'Lien copié',
  delete_post_q: 'Supprimer le post ?',
  delete_post_desc: 'Cette action est irréversible.',
  edit_post: 'Modifier le post',
  caption: 'Légende',
  save_changes: 'Enregistrer',
  tagged_people: 'Personnes identifiées',
  report_reason_placeholder: 'Pourquoi signales-tu ce post ?',
  report: 'Signaler',
  with_label: 'Avec',
  reposted_label: 'a reposté',
  now: 'maintenant',

  role_player: 'Joueur',
  role_coach: 'Coach',
  role_club: 'Club',
  role_recruiter: 'Recruteur',
  role_media: 'Média',
  role_community: 'Communauté',
  role_admin: 'Admin',

  offer_type_tryout: 'Essai',
  offer_type_recruiting: 'Recrutement',
  offer_type_camp: 'Stage',
  offer_type_event: 'Événement',
  offer_type_showcase: 'Showcase',
  offer_type_tournament: 'Tournoi',

  plan_free: 'Gratuit',
  plan_pro: 'Pro',
  plan_elite: 'Elite',
  plan_club: 'Club',
  popular: 'Populaire',
  step_2_of_2: 'Étape 2 sur 2',
  choose_plan: 'Choisis ton plan',
  change_plan_later: 'Tu peux changer plus tard dans les Paramètres.',

  edit_your_identity: 'Modifie ton identité',
  build_player_identity: 'Construis ton identité',
  edit_profile_subtitle: 'Mets à jour ton profil Nextchamp.',
  setup_profile_subtitle: 'Configure ton profil. Démarque-toi sur le terrain.',
  identity: 'Identité',
  role: 'Rôle',
  locked: 'Verrouillé',
  username: 'Nom d\'utilisateur',
  bio: 'Bio',
  bio_placeholder: 'Ton histoire…',
  city_country: 'Ville, Pays',
  required: 'Requis',
  profile_updated: 'Profil mis à jour !',
  profile_created: 'Profil créé !',
  performance_profile: 'Profil de performance',
  coaching_profile: 'Profil de coaching',
  specialties: 'Spécialités',
  coaching_diploma: 'Diplôme de coaching (optionnel)',
  upload_diploma_desc: 'Téléverse ton diplôme pour obtenir un badge coach certifié.',
  diploma_attached: 'Diplôme joint',
  pending_verification: 'Vérification en attente',
  club_registration: 'Inscription du club',
  register_your_club: 'Inscris ton club',
  register_club_subtitle: 'Complète toutes les sections. La preuve d\'existence est obligatoire.',
  club_account_created: 'Compte club créé — vérification en attente.',
  select: 'Sélectionner',


  teams: 'équipes',
  games: 'matchs',
  game: 'match',
  person: 'personne',
  people: 'personnes',
  loading: 'Chargement…',
  back: 'Retour',

  cat_training: 'Entraînement',
  cat_highlight: 'Highlight',
  cat_match: 'Match',
  cat_announcement: 'Annonce',
  cat_recruiting: 'Recrutement',
  cat_player_spotlight: 'Spotlight joueur',
  cat_basketball_news: 'Actu basket',
  cat_event_coverage: 'Couverture d\'événement',
  cat_tournament: 'Tournoi',
  cat_highlights: 'Highlights',
  cat_community_update: 'Actu communauté',
  cat_breaking_news: 'Info de dernière minute',
  cat_recruiting_opportunity: 'Opportunité de recrutement',
  cat_prospect_watch: 'Prospects à suivre',
  cat_scouting_notes: 'Notes de scouting',
  cat_tryout: 'Essai',
  cat_showcase_opportunity: 'Opportunité showcase',
  cat_training_tips: 'Conseils d\'entraînement',
  cat_workout: 'Séance',
  cat_player_development: 'Développement joueur',
  cat_camp: 'Stage',
  cat_motivation: 'Motivation',

  dm_limit_reached: 'Limite de DM atteinte. Passe Pro pour des conversations illimitées.',
  dm_limit_reached_free: 'Tu as atteint ta limite de conversations pour ce mois.',
  upgrade: 'Upgrader',
  password_change_soon: 'Changement de mot de passe bientôt disponible',
  account_deleted: 'Compte supprimé (démo)',
  payment_coming_soon: 'Paiement bientôt disponible. Tu pourras upgrader plus tard depuis les Paramètres.',
};

export const translations: Record<Language, TranslationKeys> = { en, fr };

// ─── Dynamic translation helpers ────────────────────────────────────────────
// Map canonical English values (stored in data) to translation keys.

const CATEGORY_KEY_MAP: Record<string, keyof TranslationKeys> = {
  'Training': 'cat_training',
  'Highlight': 'cat_highlight',
  'Match': 'cat_match',
  'Announcement': 'cat_announcement',
  'Recruiting': 'cat_recruiting',
  'Player Spotlight': 'cat_player_spotlight',
  'Basketball News': 'cat_basketball_news',
  'Event Coverage': 'cat_event_coverage',
  'Tournament': 'cat_tournament',
  'Highlights': 'cat_highlights',
  'Community Update': 'cat_community_update',
  'Breaking News': 'cat_breaking_news',
  'Recruiting Opportunity': 'cat_recruiting_opportunity',
  'Prospect Watch': 'cat_prospect_watch',
  'Scouting Notes': 'cat_scouting_notes',
  'Tryout': 'cat_tryout',
  'Showcase Opportunity': 'cat_showcase_opportunity',
  'Training Tips': 'cat_training_tips',
  'Workout': 'cat_workout',
  'Player Development': 'cat_player_development',
  'Camp': 'cat_camp',
  'Motivation': 'cat_motivation',
};

export function translateCategory(category: string | undefined, t: TranslationKeys): string {
  if (!category) return '';
  const key = CATEGORY_KEY_MAP[category];
  return key ? (t[key] as string) : category;
}

const ROLE_KEY_MAP: Record<string, keyof TranslationKeys> = {
  player: 'role_player',
  coach: 'role_coach',
  club: 'role_club',
  recruiter: 'role_recruiter',
  media: 'role_media',
  community: 'role_community',
  admin: 'role_admin',
};

export function translateRole(role: string | undefined, t: TranslationKeys): string {
  if (!role) return '';
  const key = ROLE_KEY_MAP[role];
  return key ? (t[key] as string) : role;
}

const OFFER_TYPE_KEY_MAP: Record<string, keyof TranslationKeys> = {
  tryout: 'offer_type_tryout',
  recruiting: 'offer_type_recruiting',
  camp: 'offer_type_camp',
  event: 'offer_type_event',
  showcase: 'offer_type_showcase',
  tournament: 'offer_type_tournament',
};

export function translateOfferType(type: string | undefined, t: TranslationKeys): string {
  if (!type) return '';
  const key = OFFER_TYPE_KEY_MAP[type];
  return key ? (t[key] as string) : type;
}

const PLAN_KEY_MAP: Record<string, keyof TranslationKeys> = {
  free: 'plan_free',
  pro: 'plan_pro',
  elite: 'plan_elite',
  club: 'plan_club',
};

export function translatePlan(plan: string | undefined, t: TranslationKeys): string {
  if (!plan) return '';
  const key = PLAN_KEY_MAP[plan];
  return key ? (t[key] as string) : plan;
}
